/**
 * Corvenio Enterprise Error Normalization Utility
 * 
 * Safely converts any unknown API error, Axios error, DRF error object,
 * Error instance, string, array, or validation object into a clean,
 * human-readable string without throwing runtime exceptions.
 */

export function safeToLowerCase(val: unknown): string {
  if (typeof val === "string") {
    return val.toLowerCase();
  }
  if (val === null || val === undefined) {
    return "";
  }
  try {
    return String(val).toLowerCase();
  } catch {
    return "";
  }
}

export function normalizeApiError(error: unknown): string {
  const DEFAULT_MESSAGE = "Something went wrong. Please try again.";

  if (error === null || error === undefined) {
    return DEFAULT_MESSAGE;
  }

  // 1. Primitive string
  if (typeof error === "string") {
    const trimmed = error.trim();
    if (!trimmed) return DEFAULT_MESSAGE;
    if (trimmed === "Network Error") {
      return "API unavailable. Unable to connect to backend server. Please check network connection.";
    }
    // Suppress raw Django/HTML debug output in production UI
    if (trimmed.startsWith("<!DOCTYPE") || trimmed.startsWith("<html") || trimmed.includes("Traceback (most recent call last)")) {
      return DEFAULT_MESSAGE;
    }
    return trimmed;
  }

  // 2. Axios Error / DRF Response Object Payload (Check response data FIRST before generic Error.message)
  if (typeof error === "object" && error !== null) {
    const errObj = error as Record<string, any>;
    const status = errObj.response?.status;
    const data = errObj.response?.data || errObj.data;

    // Check for Axios network-level failures (CORS, network offline, backend unreachable)
    if (errObj.isAxiosError || errObj.code || errObj.message === "Network Error") {
      if (errObj.code === "ECONNABORTED") {
        return "Request timed out. Please try again.";
      }
      if (errObj.code === "ERR_NETWORK" || errObj.message === "Network Error" || (!errObj.response && errObj.request)) {
        return "API unavailable. Unable to connect to backend server. Please check your connection or CORS settings.";
      }
    }

    if (data) {
      if (typeof data === "string") {
        const trimmed = data.trim();
        if (trimmed && !trimmed.startsWith("<!DOCTYPE") && !trimmed.startsWith("<html") && !trimmed.includes("Traceback (most recent call last)")) {
          return trimmed;
        }
      } else if (typeof data === "object" && data !== null) {
        // Priority 1: Direct detail/message/error properties
        if (data.detail) {
          return normalizeApiError(data.detail);
        }
        if (data.error) {
          return normalizeApiError(data.error);
        }
        if (data.message) {
          return normalizeApiError(data.message);
        }
        if (data.non_field_errors) {
          return normalizeApiError(data.non_field_errors);
        }

        // Priority 2: Field validation objects (e.g. { email: ["Invalid format"], password: ["Too short"] })
        const fields = data.errors || data;
        if (typeof fields === "object" && fields !== null) {
          const messages: string[] = [];
          for (const [key, val] of Object.entries(fields)) {
            if (["status", "code", "statusCode", "success"].includes(key)) continue;
            const valStr = normalizeApiError(val);
            if (valStr && valStr !== DEFAULT_MESSAGE) {
              messages.push(`${key}: ${valStr}`);
            }
          }
          if (messages.length > 0) {
            return messages.join(". ");
          }
        }
      }
    }

    // Status code fallbacks if data was empty or unparseable
    if (status) {
      if (status === 401) return "Authentication required. Please sign in.";
      if (status === 403) return "Permission denied.";
      if (status === 404) return "Registration endpoint or resource unavailable (404).";
      if (status >= 500) return "Server error. Please try again later.";
    }
  }

  // 3. Standard JS Error (Fallback if no server response data was attached)
  if (error instanceof Error) {
    const msg = error.message;
    if (typeof msg === "string" && msg.trim()) {
      if (msg === "Network Error") {
        return "API unavailable. Unable to connect to backend server.";
      }
      if (msg.startsWith("<!DOCTYPE") || msg.startsWith("<html")) {
        return DEFAULT_MESSAGE;
      }
      return msg.trim();
    }
  }

  // 4. Generic object message property fallback
  if (typeof error === "object" && error !== null) {
    const errObj = error as Record<string, any>;
    if (errObj.message && typeof errObj.message === "string") {
      if (errObj.message === "Network Error") {
        return "API unavailable. Unable to connect to backend server.";
      }
      return errObj.message;
    }
  }

  return DEFAULT_MESSAGE;
}
